import React from 'react';
import type { Config, Layout } from 'plotly.js';
import { SxProps } from '@mui/material';
import { useAuth } from 'hooks/AuthContext';
import { useAppState } from 'hooks/GlobalContext';
import * as Types from 'types/types';

type Metadata = Record<string, string | number>;

const emptyQuerySet: Types.ChromaDBGetResponse = {
  ids: [],
  documents: [],
  metadatas: [],
  embeddings: [],
  distances: [],
  name: 'Empty',
  size: 0,
  dimensions: 2,
  query: '',
};

interface PlotData {
  name: string;
  mode: string;
  type: string;
  x: number[];
  y: number[];
  z?: number[];
  text: string[];
  marker: {
    color: string[];
    size: number[];
    symbol: string;
    opacity: number;
  };
  customdata: Metadata[];
  hovertemplate: string;
}

type Node = {
  id: string;
  content: string;
  fullContent: string | undefined;
  emoji: string;
  docType: string;
  source_file: string;
  distance: number | undefined;
  path: string;
  chunkBegin: number;
  lineBegin: number;
  chunkEnd: number;
  lineEnd: number;
  sx: SxProps;
};

const config: Partial<Config> = {
  responsive: true,
  autosizable: true,
  displaylogo: false,
  showSendToCloud: false,
  staticPlot: false,
  frameMargins: 0,
  scrollZoom: false,
  doubleClick: false,
  modeBarButtonsToRemove: ['lasso2d', 'select2d'],
};

const layout: Partial<Layout> = {
  autosize: false,
  clickmode: 'event+select',
  paper_bgcolor: '#FFFFFF',
  plot_bgcolor: '#FFFFFF',
  font: { family: 'Roboto, sans-serif', color: '#2E2E2E' },
  hovermode: 'closest',
  scene: {
    bgcolor: '#FFFFFF',
    zaxis: { title: 'Z', gridcolor: '#cccccc', zerolinecolor: '#aaaaaa' },
  },
  xaxis: { title: 'X', gridcolor: '#cccccc', zerolinecolor: '#aaaaaa' },
  yaxis: { title: 'Y', gridcolor: '#cccccc', zerolinecolor: '#aaaaaa' },
  margin: { r: 0, b: 0, l: 0, t: 0 },
  legend: { x: 0.8, y: 0, xanchor: 'left', yanchor: 'top', orientation: 'h' },
  showlegend: true,
};

const emojiMap: Record<string, string> = {
  query: '🔍',
  resume: '📄',
  projects: '📁',
  jobs: '📁',
  'performance-reviews': '📄',
  news: '📰',
};

const colorMap: Record<string, string> = {
  query: '#D4A017',
  resume: '#4A7A7D',
  projects: '#1A2536',
  news: '#D3CDBF',
  'performance-reviews': '#8FD0D0',
  jobs: '#F3aD8F',
};

interface SendQueryDeps {
  apiClient: ReturnType<typeof useAuth>['apiClient'];
  setQuerySet: React.Dispatch<React.SetStateAction<Types.ChromaDBGetResponse>>;
  setSnack: ReturnType<typeof useAppState>['setSnack'];
  onQueryResult?: (query: string, filenames: string[]) => void;
  isControlled: boolean;
  setInternalQuery: React.Dispatch<React.SetStateAction<string>>;
}

function normalizeDimension(arr: number[]): number[] {
  const min = Math.min(...arr);
  const max = Math.max(...arr);
  const range = max - min;
  if (range === 0) return arr.map(() => 0.5);
  return arr.map(v => (v - min) / range);
}

async function sendQuery(queryText: string, deps: SendQueryDeps): Promise<void> {
  if (!queryText.trim()) return;
  const { apiClient, setQuerySet, setSnack, onQueryResult, isControlled, setInternalQuery } = deps;
  try {
    const result = await apiClient.getCandidateSimilarContent(queryText);
    setQuerySet(result);
    const uniqueSourceFiles = result.metadatas
      .map((x: { sourceFile?: string }) => x.sourceFile)
      .filter((sf, idx, arr): sf is string => sf !== undefined && arr.indexOf(sf) === idx);
    onQueryResult?.(queryText, uniqueSourceFiles || []);
    if (!isControlled) setInternalQuery('');
  } catch {
    setSnack(`Error obtaining similar content to ${queryText}.`, 'error');
  }
}

function buildPlotData(
  result: Types.ChromaDBGetResponse,
  view2D: boolean,
  querySet: Types.ChromaDBGetResponse,
  filenameFilter: string[]
): PlotData[] | null {
  const grouped = splitResult(result, view2D, querySet, filenameFilter);
  if (!grouped) return null;
  const { query, filtered, is3D } = grouped;
  const hasQuery = query.metadatas.length > 0;
  const filteredSizes = filtered.metadatas.map(() => (hasQuery ? 2 : 6));
  const querySizes = query.metadatas.map(m => 6 + 12 * Math.pow(1 - (m.distance || 1), 3));
  const data: PlotData[] = [
    traceFor('All data', filtered, filteredSizes, is3D, '&nbsp;'),
    traceFor('Query', query, querySizes, is3D, '%{text}'),
  ];
  if (is3D) {
    data[0].z = normalizeDimension(filtered.embeddings.map((v: number[]) => v[2]));
    data[1].z = normalizeDimension(query.embeddings.map((v: number[]) => v[2]));
  }
  return data;
}

function traceFor(
  name: string,
  data: Types.ChromaDBGetResponse,
  sizes: number[],
  is3D: boolean,
  hovertemplate: string
): PlotData {
  const docTypes = data.metadatas.map(m => m.docType || 'unknown');
  return {
    name,
    x: normalizeDimension(data.embeddings.map((v: number[]) => v[0])),
    y: normalizeDimension(data.embeddings.map((v: number[]) => v[1])),
    mode: 'markers',
    marker: {
      size: sizes,
      symbol: 'circle',
      color: docTypes.map(type => colorMap[String(type)] || '#4d4d4d'),
      opacity: 1,
    },
    text: data.ids,
    customdata: data.metadatas,
    type: is3D ? 'scatter3d' : 'scatter',
    hovertemplate,
  };
}

function splitResult(
  result: Types.ChromaDBGetResponse,
  view2D: boolean,
  querySet: Types.ChromaDBGetResponse,
  filenameFilter: string[]
) {
  if (!result.embeddings?.length) return null;
  const full = {
    ...result,
    ids: [...(result.ids || [])],
    documents: [...(result.documents || [])],
    embeddings: [...result.embeddings],
    metadatas: [...(result.metadatas || [])],
  };
  const is2D = full.embeddings.every((v: number[]) => v.length === 2);
  const is3D = full.embeddings.every((v: number[]) => v.length === 3);
  if ((view2D && !is2D) || (!view2D && !is3D) || (!is2D && !is3D)) return null;
  const emptySet = (): Types.ChromaDBGetResponse => ({
    ids: [],
    documents: [],
    embeddings: [],
    metadatas: [],
    distances: [],
    query: '',
    size: 0,
    dimensions: 2,
    name: '',
  });
  const query = emptySet();
  const filtered = emptySet();
  full.ids.forEach((id, index) =>
    classifyVectorItem(id, index, full, querySet, filenameFilter, query, filtered)
  );
  prependUmapQueryPoint(query, querySet, view2D);
  return { query, filtered, is3D };
}

function classifyVectorItem(
  id: string,
  index: number,
  full: Types.ChromaDBGetResponse,
  querySet: Types.ChromaDBGetResponse,
  filenameFilter: string[],
  query: Types.ChromaDBGetResponse,
  filtered: Types.ChromaDBGetResponse
): void {
  if (filenameFilter.length > 0) {
    const sourceFile = full.metadatas[index]?.sourceFile;
    if (!sourceFile || !filenameFilter.includes(sourceFile)) return;
  }
  const foundIndex = querySet.ids.indexOf(id);
  full.metadatas[index].id = id;
  full.metadatas[index].content = full.documents[index];
  if (foundIndex !== -1) {
    full.metadatas[index].distance = querySet.distances?.[foundIndex];
    query.ids.push(id);
    query.documents.push(full.documents[index]);
    query.embeddings.push(full.embeddings[index]);
    query.metadatas.push(full.metadatas[index]);
  } else {
    full.metadatas[index].distance = undefined;
    filtered.ids.push(id);
    filtered.documents.push(full.documents[index]);
    filtered.embeddings.push(full.embeddings[index]);
    filtered.metadatas.push(full.metadatas[index]);
  }
}

function prependUmapQueryPoint(
  query: Types.ChromaDBGetResponse,
  querySet: Types.ChromaDBGetResponse,
  view2D: boolean
): void {
  const embedding = view2D ? querySet.umapEmbedding2D : querySet.umapEmbedding3D;
  if (!embedding?.length) return;
  query.ids.unshift('query');
  query.metadatas.unshift({
    id: 'query',
    docType: 'query',
    content: querySet.query || '',
    distance: 0,
  });
  query.embeddings.unshift(embedding);
}

export type { Metadata, Node, PlotData, SendQueryDeps };
export { buildPlotData, colorMap, config, emojiMap, emptyQuerySet, layout, sendQuery };
